/**
 * Floating embers — the hero's continuous ambient layer.
 *
 * Sixteen gold motes drifting up out of the lower third of the frame, as if
 * off the butter lamps the page is about. Deliberately cheap: the trajectories
 * are baked into two CSS keyframes and every mote is a composited
 * `transform`/`opacity` animation on its own layer, so nothing here touches
 * the main thread after the first paint. The per-ember variation (start x,
 * size, opacity, duration, delay, drift) is a fixed table rather than
 * `Math.random()` so server and client render the same thing and the
 * composition is the one that was designed, not a new one every reload.
 *
 * `prefers-reduced-motion` kills the whole layer in `base.css`'s blanket rule;
 * `pages.css` additionally hides it so it does not freeze mid-flight as a
 * scatter of static dots.
 */

/**
 * x%, size(px), peak opacity, duration(s), delay(s), sway(px), rise(px), hue.
 *
 * Sizes 1.5-3.5px, durations 6-12s, rises 120-240px, and delays spread across
 * the longest duration so the layer is never empty and never pulses in unison.
 */
const EMBERS = [
  [6, 2, 0.42, 9, 0.4, 14, 180, '#F5C518'],
  [12, 3, 0.55, 7.5, 3.2, -10, 150, '#FFB020'],
  [19, 1.5, 0.3, 11.5, 6.4, 18, 220, '#FF9900'],
  [26, 2.5, 0.6, 6.5, 1.6, -16, 140, '#F5C518'],
  [33, 2, 0.38, 10, 8.1, 12, 200, '#FFB020'],
  [39, 3.5, 0.58, 8, 4.4, -20, 165, '#FF9900'],
  [46, 1.5, 0.26, 12, 2.1, 9, 240, '#F5C518'],
  [53, 2.5, 0.5, 7, 9.3, -13, 145, '#FFB020'],
  [59, 2, 0.44, 10.5, 5.2, 16, 210, '#FF9900'],
  [66, 3, 0.6, 6, 0.9, -11, 130, '#F5C518'],
  [73, 1.5, 0.28, 11, 7.2, 20, 230, '#FFB020'],
  [79, 2.5, 0.48, 8.5, 2.8, -15, 175, '#FF9900'],
  [86, 2, 0.36, 9.5, 10.2, 10, 195, '#F5C518'],
  [92, 3, 0.54, 7, 5.9, -18, 155, '#FFB020'],
  [97, 1.5, 0.24, 12, 3.7, 13, 235, '#FF9900'],
  [3, 2.5, 0.46, 8, 6.8, -12, 160, '#F5C518'],
];

export default function Embers() {
  return (
    <div className="embers" aria-hidden="true">
      {EMBERS.map(([x, size, opacity, duration, delay, sway, rise, hue], index) => (
        <span
          key={index}
          className="ember"
          style={{
            left: `${x}%`,
            width: size,
            height: size,
            '--ember-opacity': opacity,
            '--ember-duration': `${duration}s`,
            '--ember-delay': `${delay}s`,
            '--ember-sway': `${sway}px`,
            '--ember-rise': `${rise}px`,
            '--ember-hue': hue,
          }}
        />
      ))}
    </div>
  );
}
